/**
 * Wagamour agentic analytics — GA4 events with consistent context:
 * user_id (when logged in), platform, ISO timestamp, session_id.
 * Use typed helpers + dedupe keys to avoid duplicate fires (React Strict Mode / rerenders).
 */

import { APP_ENTER_GTAG_TIMEOUT_MS, getAnalyticsPagePath, trackEvent } from '@/lib/analytics'
import { whenGtagReady } from '@/lib/analytics/gaConfig'
import { whenWagamourScreenContextReady } from '@/lib/analytics/wagamourScreenPath'
import { isStandalone } from '@/lib/pwa/isStandalone'

const SESSION_ID_KEY = 'wagamour_analytics_session_id'
const USER_ID_KEY = 'wagamour_analytics_user_id'

export type WagamourPlatform = 'web' | 'pwa' | 'android'

declare global {
  interface Window {
    WagamourNative?: { platform?: string }
  }
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `wm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`
}

/** Stable per browser tab session (sessionStorage). */
export function ensureAnalyticsSessionId(): string {
  if (typeof window === 'undefined') return ''
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id = randomId()
      sessionStorage.setItem(SESSION_ID_KEY, id)
    }
    return id
  } catch {
    return ''
  }
}

export function getAnalyticsUserId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const id = sessionStorage.getItem(USER_ID_KEY)
    return id && id.length > 0 ? id : undefined
  } catch {
    return undefined
  }
}

/** Persisted by `setGaUserId` in `lib/analytics.ts` — call that from auth sync. */
export function setAnalyticsUserId(userId: string | null | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (userId) sessionStorage.setItem(USER_ID_KEY, userId)
    else sessionStorage.removeItem(USER_ID_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * web: in-browser tab (not installed PWA)
 * pwa: installed PWA / standalone display mode
 * android: native shell — set `window.WagamourNative = { platform: 'android' }` from WebView
 */
export function getWagamourPlatform(): WagamourPlatform {
  if (typeof window === 'undefined') return 'web'
  if (window.WagamourNative?.platform === 'android') return 'android'
  if (isStandalone()) return 'pwa'
  return 'web'
}

/** Human-readable open mode for `app_opened_mode`. */
export function getAppOpenedModeLabel(): 'browser' | 'standalone' | 'android_native' {
  const p = getWagamourPlatform()
  if (p === 'android') return 'android_native'
  if (p === 'pwa') return 'standalone'
  return 'browser'
}

function baseParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const session_id = ensureAnalyticsSessionId()
  const platform = getWagamourPlatform()
  const timestamp = new Date().toISOString()
  const user_id = getAnalyticsUserId()
  const page_path =
    typeof window !== 'undefined' ? getAnalyticsPagePath() : ''

  return {
    ...(session_id ? { session_id } : {}),
    platform,
    timestamp,
    ...(user_id ? { user_id } : {}),
    page_path,
    ...(extra || {}),
  }
}

/**
 * Fire a GA4 event with agentic context. Prefer `dedupeKey` for one-shot events
 * (stored in sessionStorage as `wagamour_agentic_dedupe_<key>`).
 */
export function trackAgenticEvent(
  name: string,
  params?: Record<string, unknown>,
  options?: { dedupeKey?: string }
): void {
  if (typeof window === 'undefined') return

  if (options?.dedupeKey) {
    const flag = `wagamour_agentic_dedupe_${options.dedupeKey}`
    try {
      if (sessionStorage.getItem(flag) === '1') return
      sessionStorage.setItem(flag, '1')
    } catch {
      /* if storage blocked, still emit once */
    }
  }

  const merged = baseParams(params)
  if (process.env.NODE_ENV === 'development') {
    console.log('[AgenticAnalytics]', name, merged)
  }
  trackEvent(name, merged as Record<string, any>)
}

// --- Core agentic funnel ---

export function trackPicksGenerated(input: {
  surface: 'picks_tab' | 'today_plan'
  sections_count?: number
  items_count?: number
  plan_id?: string
  plan_kind?: string
  cache_source?: string
  dedupeKey: string
}): void {
  trackAgenticEvent(
    'picks_generated',
    {
      surface: input.surface,
      sections_count: input.sections_count,
      items_count: input.items_count,
      plan_id: input.plan_id,
      plan_kind: input.plan_kind,
      cache_source: input.cache_source,
    },
    { dedupeKey: `picks_generated_${input.dedupeKey}` }
  )
}

export function trackDecisionPresented(input: {
  surface: 'picks_tab' | 'today_plan' | 'product_card'
  plan_id?: string
  decision_id?: string
  place_id?: string
  dedupeKey: string
}): void {
  trackAgenticEvent(
    'decision_presented',
    {
      surface: input.surface,
      plan_id: input.plan_id,
      decision_id: input.decision_id,
      place_id: input.place_id,
    },
    { dedupeKey: `decision_presented_${input.dedupeKey}` }
  )
}

export function trackDecisionClicked(input: {
  action_type: string
  place_id?: string
  title?: string
  source?: string
  plan_id?: string
}): void {
  trackAgenticEvent('decision_clicked', {
    action_type: input.action_type,
    place_id: input.place_id,
    title: input.title,
    source: input.source,
    plan_id: input.plan_id,
  })
}

export function trackActionExecuted(input: {
  action_type: string
  place_id?: string
  title?: string
}): void {
  trackAgenticEvent('action_executed', {
    action_type: input.action_type,
    place_id: input.place_id,
    title: input.title,
  })
}

export function trackOutcomeRecorded(input: {
  kind: string
  action_type: string
  place_id?: string
  entity_id?: string
}): void {
  trackAgenticEvent('outcome_recorded', {
    outcome_kind: input.kind,
    action_type: input.action_type,
    place_id: input.place_id,
    entity_id: input.entity_id,
  })
}

// --- Play ---

export function trackPlayRequestSent(input: {
  target_dog_id: string
  dog_name?: string
}): void {
  trackAgenticEvent('play_request_sent', {
    target_dog_id: input.target_dog_id,
    dog_name: input.dog_name,
  })
}

export function trackPlayRequestAccepted(input: {
  request_id: string
  from_user_id?: string
}): void {
  trackAgenticEvent('play_request_accepted', {
    request_id: input.request_id,
    from_user_id: input.from_user_id,
  })
}

export function trackPlaySessionCompleted(input: {
  request_id: string
  target_dog_id?: string
  role: 'sender' | 'recipient'
}): void {
  trackAgenticEvent(
    'play_session_completed',
    {
      request_id: input.request_id,
      target_dog_id: input.target_dog_id,
      role: input.role,
    },
    { dedupeKey: `play_session_completed_${input.request_id}` }
  )
}

// --- Parks ---

export function trackParkCheckinAgentic(input: {
  park_id: string
  park_name: string
}): void {
  trackAgenticEvent('park_checkin', {
    park_id: input.park_id,
    park_name: input.park_name,
  })
}

export function trackParkExitAgentic(input: {
  park_id: string
  park_name: string
  visit_duration_seconds: number
}): void {
  trackAgenticEvent('park_exit', {
    park_id: input.park_id,
    park_name: input.park_name,
    visit_duration_seconds: input.visit_duration_seconds,
  })
}

export function trackParkVisitDurationAgentic(input: {
  park_id: string
  park_name: string
  visit_duration_seconds: number
}): void {
  trackAgenticEvent('park_visit_duration', {
    park_id: input.park_id,
    park_name: input.park_name,
    visit_duration_seconds: input.visit_duration_seconds,
  })
}

// --- App / PWA / Android shell ---

export function trackAppInstallPromptShown(input: { install_surface: string }): void {
  trackAgenticEvent(
    'app_install_prompt_shown',
    { install_surface: input.install_surface },
    { dedupeKey: 'app_install_prompt_shown' }
  )
}

export function trackAppInstallCompleted(input: { install_surface: string }): void {
  trackAgenticEvent(
    'app_install_completed',
    { install_surface: input.install_surface },
    { dedupeKey: 'app_install_completed' }
  )
}

export function trackAppOpenedMode(): void {
  const mode = getAppOpenedModeLabel()
  if (typeof window === 'undefined') return
  void (async () => {
    const started = Date.now()
    const gtagReady = await whenGtagReady({
      timeoutMs: APP_ENTER_GTAG_TIMEOUT_MS,
      intervalMs: 50,
    })
    if (!gtagReady) return
    // Remaining budget of the same 12s bound: gtag + V2 screen, not 12s+12s.
    const remainingMs = Math.max(
      0,
      APP_ENTER_GTAG_TIMEOUT_MS - (Date.now() - started)
    )
    await whenWagamourScreenContextReady({
      timeoutMs: remainingMs,
      intervalMs: 50,
    })
    // If still on `/app-beta` with no screen, emit once with the shell path
    // rather than retrying. Dedupe is applied inside trackAgenticEvent.
    trackAgenticEvent(
      'app_opened_mode',
      { mode },
      { dedupeKey: `app_opened_mode_${mode}` }
    )
  })()
}
